import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { masks } from "@/app/utils/masks";
import { format } from "date-fns";
import { crefisaService } from "@/app/services/crefisaService";
import { PartnerType } from "@/app/utils/partners.enum";
import { PostTransactionPayload } from "@/app/services/crefisaService/postTransaction";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/view/components/ui/use-toast";

const getProposalReviewModalSchema = z.object({});

type getProposalReviewModalFormData = z.infer<
  typeof getProposalReviewModalSchema
>;

interface UseProposalReviewModalProps {
  onContinue?: () => void;
}

export default function useProposalReviewModal({
  onContinue,
}: UseProposalReviewModalProps) {
  const { toast } = useToast();
  const { customerData, proposalData, simulationData, setProposal } =
    useCrefisaContext();

  const getProposalReviewModalForm = useForm<getProposalReviewModalFormData>({
    resolver: zodResolver(getProposalReviewModalSchema),
    mode: "onChange",
  });

  const formatAccountType = (type?: string) => {
    switch (type) {
      case "CP":
        return "Poupança";
      case "CC":
        return "Conta corrente";
      default:
        return "-";
    }
  };

  const personalFields = [
    { label: "Nome", value: customerData?.name },
    { label: "CPF", value: masks.cpf(customerData?.cpf) },
    { label: "RG", value: masks.rg(customerData?.rg?.number || "") },
    {
      label: "Data de emissão",
      value: format(new Date(customerData?.rg?.date || ""), "dd/MM/yyyy"),
    },
  ];

  const bankFields = [
    {
      label: "Tipo de conta",
      value: formatAccountType(customerData?.bankAccount?.accountType),
    },
    { label: "Banco", value: customerData?.bankAccount?.bank },
    {
      label: "Agência",
      value: masks.bankAgency(customerData?.bankAccount?.agency),
    },
    {
      label: "Conta",
      value: masks.bankAccount(customerData?.bankAccount?.account),
    },
  ];

  const { handleSubmit } = getProposalReviewModalForm;

  const { mutateAsync: createTransaction, isPending } = useMutation({
    mutationFn: (data: PostTransactionPayload) =>
      crefisaService.postTransaction(data),
  });

  function getPhone(phone?: string) {
    const ddd = phone?.substring(0, 2);
    const number = phone?.substring(2);

    return {
      ddd: ddd ? Number(ddd) : undefined,
      phone: number ? Number(number) : undefined,
    };
  }

  const submitModal = handleSubmit(async () => {
    try {
      if (
        proposalData?.value == null ||
        proposalData?.prazo == null ||
        !customerData?.customerId ||
        !simulationData?.clientId ||
        (!proposalData?.offerId && !proposalData?.simulationId)
      ) {
        toast({
          title: "Dados incompletos",
          description: "Revise os dados antes de prosseguir.",
          variant: "destructive",
        });
        return;
      }

      const { ddd, phone } = getPhone(customerData?.phone);

      const payload: PostTransactionPayload = {
        value: proposalData.value,
        customerId: customerData.customerId,
        Prazo: proposalData.prazo,
        type: PartnerType.CREFISA,
        clientId: simulationData.clientId,
        ...(proposalData.offerId
          ? {
              offerId: proposalData.offerId,
              ddd,
              phone,
            }
          : {
              simulationId: proposalData.simulationId,
              refinancingId: proposalData.refinancingId || "",
              refinRecurring: simulationData.isRefinancing,
            }),
      };

      const response = await createTransaction(payload);

      if (!response?.contract) {
        toast({
          title: "Erro ao enviar proposta",
          description: "Falha ao enviar proposta. Tente novamente mais tarde.",
          variant: "destructive",
        });

        return;
      }

      setProposal({
        contract: response.contract,
      });

      onContinue?.();
    } catch (error) {
      toast({
        title: "Erro ao enviar proposta",
        description: "Erro inesperado. Tente novamente mais tarde.",
        variant: "destructive",
      });
      console.error(error);
    }
  });

  return {
    form: getProposalReviewModalForm,
    submitModal,
    personalFields,
    bankFields,
    isPending,
    proposalData,
  };
}
